/**
 * Typed Stytch webhook event catalog. Stytch ships Standard Webhooks
 * (Svix) for both Consumer and B2B project types — the catalog
 * covers events from BOTH verticals; the connection's stored
 * `stytchProject` ('consumer' | 'b2b') filters which events the
 * adapter normalizes for this brand.
 *
 * Hard schema invariant: every entry's payload schema includes
 * `{ key: 'email', type: 'string', required: true }`. When an event lacks an
 * email, the processor resolves the object through the tenant identity ledger
 * or records `unresolved_recipient` without starting an automation.
 */

import type {
  InboundEventCategory,
  InboundEventMetadata,
} from '@/lib/integrations/inbound/types'
import type {
  IntegrationTriggerField,
  IntegrationTriggerPayloadSchema,
} from '@/lib/triggers/types'

type TriggerField = IntegrationTriggerField
type TriggerPayloadSchema = IntegrationTriggerPayloadSchema

export type StytchProject = 'consumer' | 'b2b'
export type SupportedStytchEventType =
  | 'direct.user.create'
  | 'direct.user.update'
  | 'direct.user.delete'
  | 'direct.organization.create'
  | 'direct.member.create'
  | 'direct.member.update'
  | 'direct.member.delete'

export const SUPPORTED_STYTCH_EVENTS: ReadonlyArray<SupportedStytchEventType> =
  [
    'direct.user.create',
    'direct.user.update',
    'direct.user.delete',
    'direct.organization.create',
    'direct.member.create',
    'direct.member.update',
    'direct.member.delete',
  ] as const

const SUPPORTED_STYTCH_EVENT_SET = new Set<string>(SUPPORTED_STYTCH_EVENTS)

/**
 * Which project type each event ships from. The adapter's
 * `normalize()` filters events not in the connection's vertical so a
 * Consumer connection never fires B2B events and vice-versa.
 */
export const STYTCH_EVENT_PROJECT: Record<
  SupportedStytchEventType,
  StytchProject
> = {
  'direct.user.create': 'consumer',
  'direct.user.update': 'consumer',
  'direct.user.delete': 'consumer',
  'direct.organization.create': 'b2b',
  'direct.member.create': 'b2b',
  'direct.member.update': 'b2b',
  'direct.member.delete': 'b2b',
}

/**
 * Slim user-facing common preamble. Stytch event metadata (event id /
 * type, project type, object id/type) is intentionally absent — the
 * extractor still emits those keys into `triggerInstances.payload` for
 * the contact mapper to read.
 */
const COMMON_FIELDS: Array<TriggerField> = [
  {
    key: 'email',
    type: 'string',
    required: true,
    description:
      'Recipient email from the Stytch event or a previously observed identity. Events without either are recorded as unresolved and do not start automations.',
    exampleValue: 'alex@example.com',
  },
  {
    key: 'firstName',
    type: 'string',
    required: false,
    description: 'User first name when present on the event payload.',
    exampleValue: 'Alex',
  },
  {
    key: 'lastName',
    type: 'string',
    required: false,
    description: 'User last name when present on the event payload.',
    exampleValue: 'Morgan',
  },
  {
    key: 'createdAt',
    type: 'int',
    required: false,
    description: 'Unix timestamp (seconds) when available on the event.',
    exampleValue: 1_710_000_000,
  },
]

const USER_EXTRA_FIELDS: Array<TriggerField> = []

const ORG_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'organizationName',
    type: 'string',
    required: false,
    description: 'B2B organization display name when set.',
    exampleValue: 'Acme Inc.',
  },
  {
    key: 'organizationSlug',
    type: 'string',
    required: false,
    description: 'B2B organization slug when set.',
    exampleValue: 'acme',
  },
]

const MEMBER_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'role',
    type: 'string',
    required: false,
    description: 'Member role when present.',
    exampleValue: 'admin',
  },
]

function buildPayloadSchema(
  extras: ReadonlyArray<TriggerField>
): TriggerPayloadSchema {
  return {
    type: 'object',
    fields: [...COMMON_FIELDS, ...extras],
  }
}

function buildSamplePayload(
  _eventType: SupportedStytchEventType,
  payload: Record<string, string | number | boolean | null>
): Record<string, string | number | boolean | null> {
  return {
    email: 'alex@example.com',
    firstName: 'Alex',
    lastName: 'Morgan',
    ...payload,
  }
}

export const STYTCH_EVENT_CATEGORIES: ReadonlyArray<InboundEventCategory> = [
  { id: 'user', label: 'User' },
  { id: 'organization', label: 'Organization' },
  { id: 'member', label: 'Member' },
]

export const STYTCH_EVENT_METADATA: Array<InboundEventMetadata> = [
  {
    eventType: 'direct.user.create',
    title: 'Stytch - User Created',
    description:
      'Fires when a Stytch Consumer user is created. Use to send a welcome email or alert your team that someone signed up.',
    category: 'user',
    payloadSchema: buildPayloadSchema(USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('direct.user.create', {
      createdAt: 1_710_000_000,
    }),
    useCases: [
      {
        title: 'Send a welcome email',
        description: 'Greet brand-new signups with onboarding next steps.',
      },
      {
        title: 'Internal signup alert',
        description: 'Notify your team about new Stytch Consumer signups.',
      },
    ],
  },
  {
    eventType: 'direct.user.update',
    title: 'Stytch - User Updated',
    description:
      'Fires when a Stytch Consumer user record changes. Use to keep Brew contacts in lockstep.',
    category: 'user',
    payloadSchema: buildPayloadSchema(USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('direct.user.update', {}),
    useCases: [
      {
        title: 'Contact sync',
        description: 'Mirror profile changes into Brew contacts.',
      },
    ],
  },
  {
    eventType: 'direct.user.delete',
    title: 'Stytch - User Deleted',
    description:
      'Receives a Stytch Consumer user deletion. Stytch puts the deleted user ID in the top-level `id`. Brew resolves that ID to a previously observed email or records the event as unresolved.',
    category: 'user',
    payloadSchema: buildPayloadSchema(USER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('direct.user.delete', {}),
    useCases: [
      {
        title: 'Contact removal',
        description:
          'Mark the previously observed contact as deleted. If Brew has not observed the user email, no automation starts.',
      },
    ],
  },
  {
    eventType: 'direct.organization.create',
    title: 'Stytch - Organization Created',
    description:
      'Receives a new B2B organization from Stytch. The payload has no recipient identity, so Brew records it as unresolved and does not start an automation.',
    category: 'organization',
    payloadSchema: buildPayloadSchema(ORG_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('direct.organization.create', {
      organizationName: 'Acme Inc.',
      organizationSlug: 'acme',
      createdAt: 1_710_000_000,
    }),
    useCases: [
      {
        title: 'Recipient resolution required',
        description:
          'Brew does not start an automation from a fabricated organization email.',
      },
    ],
  },
  {
    eventType: 'direct.member.create',
    title: 'Stytch - Member Added',
    description:
      'Fires when a new member joins a B2B organization. Use to send invite confirmations or internal alerts.',
    category: 'member',
    payloadSchema: buildPayloadSchema(MEMBER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('direct.member.create', {
      role: 'member',
    }),
    useCases: [
      {
        title: 'Invite-to-team email',
        description:
          'Welcome the new teammate with workspace orientation + shortcuts.',
      },
      {
        title: 'Internal alert',
        description: 'Ping owners that someone joined the workspace.',
      },
    ],
  },
  {
    eventType: 'direct.member.update',
    title: 'Stytch - Member Updated',
    description:
      'Fires when a B2B member record changes (role, name). Use for role-change notifications.',
    category: 'member',
    payloadSchema: buildPayloadSchema(MEMBER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('direct.member.update', {
      role: 'admin',
    }),
    useCases: [
      {
        title: 'Role-change notification',
        description: 'Confirm role updates with both the member and the owner.',
      },
    ],
  },
  {
    eventType: 'direct.member.delete',
    title: 'Stytch - Member Removed',
    description:
      'Receives a member removal from a B2B organization. Brew resolves the top-level member ID to a previously observed email or records the event as unresolved.',
    category: 'member',
    payloadSchema: buildPayloadSchema(MEMBER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('direct.member.delete', {}),
    useCases: [
      {
        title: 'Offboarding email',
        description:
          'Confirm offboarding only when Brew previously observed the member email.',
      },
    ],
  },
]

export function isSupportedStytchEvent(
  eventType: string
): eventType is SupportedStytchEventType {
  return SUPPORTED_STYTCH_EVENT_SET.has(eventType)
}

export function getStytchEventsForProject(
  project: StytchProject
): Array<InboundEventMetadata> {
  return STYTCH_EVENT_METADATA.filter(
    (event) =>
      STYTCH_EVENT_PROJECT[event.eventType as SupportedStytchEventType] ===
      project
  )
}
